import React, { useState, useMemo } from "react";
import { Container, Row, Col, Badge, Spinner, Button, Table } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronRight, FaExternalLinkAlt, FaMapMarkerAlt, FaInfoCircle } from "react-icons/fa";
import DOMPurify from "dompurify";
import useProgramData from "../hooks/useProgramData";

// Layout Components
import Header from "../components/Header";
import Footer from "../components/Footer";

const CrashProgram = () => {
  const { data: apiData, loading, error } = useProgramData("crashProgramData");
  const [activeTab, setActiveTab] = useState("overview");

  const programData = useMemo(() => (apiData?.[0] || null), [apiData]);

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3 fw-bold letter-spacing-2 text-uppercase">Syncing Admissions Repository</p>
    </div>
  );

  if (error || !programData) return (
    <div className="premium-loader text-danger">
      <FaInfoCircle size={40} className="mb-3" />
      <p className="fw-bold">{error || "Program repository currently unavailable."}</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* Global Sticky Sub-Nav */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={7}>
              <h1 className="h4 fw-900 text-navy m-0">
                Crash <span className="text-primary">Program</span>
              </h1>
            </Col>
            {/* FIX: Button alignment and width container */}
            <Col xs={12} md={5} className="mt-3 mt-md-0">
              <div className="text-md-end">
                <Button 
                  variant="primary" 
                  href="https://applyadmission.iba-suk.edu.pk" 
                  target="_blank"
                  className="rounded-pill px-4 fw-bold shadow-sm inline-flex-center"
                >
                  Apply Online <FaExternalLinkAlt className="ms-2" size={12} />
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="portal-viewport py-5 bg-light-soft">
        <Container>
          <Row className="g-5">
            {/* Sidebar Navigation */}
            <Col lg={4}>
              <div className="sticky-sidebar" style={{ top: '120px' }}>
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">ACADEMIC PORTAL</div>
                  <div className="program-list">
                    <button 
                      className={`prog-item ${activeTab === 'overview' ? 'is-active' : ''}`} 
                      onClick={() => setActiveTab('overview')}
                    >
                      <div className="prog-info">
                        <span className="name">Program Overview</span>
                        <span className="meta">Dates & Description</span>
                      </div>
                      <FaChevronRight className="chevron" />
                    </button>
                    
                    <div className="card-label mt-2 px-4 py-2 bg-light small fw-bold text-muted">CAMPUS ELIGIBILITY</div>
                    {programData.eligibility?.campuses.map((campus, i) => (
                      <button 
                        key={i} 
                        className={`prog-item ${activeTab === i ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab(i)}
                      >
                        <div className="prog-info">
                          <span className="name">{campus.name}</span>
                          <span className="meta">{campus.departments.length} Departments</span>
                        </div>
                        <FaChevronRight className="chevron" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Col>

            {/* Main Content Area */}
            <Col lg={8}>
              <AnimatePresence mode="wait">
                {activeTab === 'overview' ? (
                  <motion.div 
                    key="overview" 
                    initial={{ opacity: 0, y: 15 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, y: -15 }}
                  >
                    <div className="mb-4">
                      <Badge bg="primary" className="mb-2 px-3">OFFICIAL RELEASE</Badge>
                      <h2 className="display-6 fw-bold text-navy">{programData.title}</h2>
                      <div 
                        className="lead text-muted mt-3" 
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(programData.description) }} 
                      />
                    </div>

                    {/* Timeline Table */}
                    <div className="phase-block bg-white p-4 p-lg-5 rounded-4 shadow-sm mb-4 border-top border-primary border-4">
                      <h3 className="h5 fw-800 text-primary mb-4 text-uppercase letter-spacing-1">
                        {programData.importantDates.title}
                      </h3>
                      <div className="timeline-v-stack">
                        {programData.importantDates.dates.map((item, index) => (
                          <div key={index} className="milestone-row d-flex justify-content-between align-items-center py-3 border-bottom-dashed">
                            <div className="d-flex align-items-center">
                              <div className="dot-indicator me-3" />
                              <span className="fw-700 text-navy">{item.label}</span>
                            </div>
                            <span className="date-display">{item.date}</span>
                          </div>
                        ))}
                      </div>
                      {programData.importantDates.note && (
                        <div className="mt-4 p-3 bg-light rounded-3 small text-muted italic">
                          <FaInfoCircle className="me-2 text-warning" />
                          <strong>Note:</strong> {programData.importantDates.note}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div 
                    key={activeTab} 
                    initial={{ opacity: 0, x: 20 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FaMapMarkerAlt className="text-primary" />
                        <span className="fw-bold text-muted text-uppercase small">Campus Eligibility</span>
                      </div>
                      <h2 className="display-6 fw-bold text-navy">{programData.eligibility.campuses[activeTab].name}</h2>
                    </div>

                    {programData.eligibility.campuses[activeTab].departments.map((dept, dIdx) => (
                      <div key={dIdx} className="bg-white p-4 rounded-4 shadow-sm mb-4 border-start border-primary border-4">
                        <h4 className="text-primary fw-900 mb-4 h5">{dept.name}</h4>
                        <div className="table-responsive">
                          <Table borderless hover className="align-middle">
                            <thead className="bg-light-soft text-navy border-bottom">
                              <tr>
                                <th className="py-3 px-3">S.No</th>
                                <th className="py-3">Academic Program</th>
                                <th className="py-3">Eligibility Requirements</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dept.programs.map((prog, pIdx) => (
                                <tr key={pIdx} className="border-bottom-dashed">
                                  <td className="px-3 fw-bold text-muted">{prog.id}</td>
                                  <td className="fw-700 text-navy">{prog.program}</td>
                                  <td className="small text-muted py-3">
                                    <div 
                                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(prog.criteria) }} 
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>

      <Footer />

      <style>{`
        .bg-light-soft { background: #f9fbfd; }
        .text-navy { color: #002b5a; }
        .fw-900 { font-weight: 900; }
        .fw-800 { font-weight: 800; }
        .fw-700 { font-weight: 700; }
        .letter-spacing-1 { letter-spacing: 1px; }
        .letter-spacing-2 { letter-spacing: 2px; }
        
        /* Center content inside button without forcing full width */
        .inline-flex-center {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .premium-loader { height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; }

        .sidebar-card { background: #fff; border-radius: 20px; overflow: hidden; }
        .prog-item {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          padding: 18px 25px; border: none; background: white; border-bottom: 1px solid #f1f5f9;
          text-align: left; transition: all 0.2s ease;
        }
        .prog-item.is-active { background: #002b5a; color: white; border-left: 6px solid #ffb800; }
        .prog-item:hover:not(.is-active) { background: #f8f9fa; padding-left: 30px; }
        
        .prog-info .name { display: block; font-weight: 700; font-size: 0.95rem; }
        .prog-info .meta { font-size: 0.75rem; opacity: 0.7; }
        
        .milestone-row { transition: 0.2s; }
        .dot-indicator { width: 8px; height: 8px; border-radius: 50%; background: #cbd5e0; }
        .date-display {
          background: #f0f4f8; color: #0055a4; padding: 5px 15px;
          border-radius: 50px; font-weight: 800; font-size: 0.85rem;
        }

        .border-bottom-dashed { border-bottom: 1px dashed #e2e8f0; }
        
        @media (max-width: 991px) {
          .program-list { display: flex; overflow-x: auto; padding: 15px; gap: 10px; }
          .prog-item { width: auto; border-radius: 12px; border: 1px solid #e2e8f0; white-space: nowrap; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
          .sidebar-card { background: transparent; shadow: none; }
        }
      `}</style>
    </div>
  );
};

export default CrashProgram;